import { computed, inject, Injectable, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { getCookie } from '@auth/bp-pf-auth';
import { NavigationExtras, Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { HeaderConfig } from '../data/interface/header.interface';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  private readonly appId = environment.app_id;

  private headerSubject = new BehaviorSubject<HeaderConfig | null>(null);
  header$ = this.headerSubject.asObservable();

  private headerActionSubject = new Subject<{ action: string; context?: any }>();
  headerAction$ = this.headerActionSubject.asObservable();

  constructor() { }

  /** Get tenant code from cookie */
  getTenantCode(): string {
    const tenantCode = `${this.appId}_tenant_code`;
    return getCookie(tenantCode) || '';
  }

  /** Get user email from cookie */
  getEmail(): string {
    const tenant = this.getTenantCode();
    const userEmail = `${tenant}_${this.appId}_email`;
    return getCookie(userEmail) || '';
  }

  router = inject(Router)

  navigateToURLWithtenantCode(path: string, extras?: NavigationExtras): void {
    const tenantCode = getCookie(`${environment.app_id}_tenant_code`);
    this.router.navigate([`/${tenantCode}/${path}`], extras);
  }

sanitizeInput(value: string, options: any = {}): string {
  if (value == null) return value;

  let result = value;

  //  PRIORITY RULES (exclusive)
  if (options.lettersOnly) {
    result = result.replace(/[^a-zA-Z\s]/g, '');
  } 
  else if (options.digitsOnly) {
    result = result.replace(/\D+/g, '').replace(/^0+(?=\d)/, '');
  } 
  else if (options.decimalAllowed) {
    result = result
      .replace(/[^0-9.]/g, '')
      .replace(/(\..*)\./g, '$1');
  } 
  else if (options.alphanumeric || options.noSpecialChars) {
    result = result.replace(/[^a-zA-Z0-9\s]/g, '');
  }

  //  Secondary cleanup
  if (options.allowSingleSpaces) {
    result = result.replace(/\s{2,}/g, ' ');
  }

  if (options.noSpaces) {
    result = result.replace(/\s+/g, '');
  }

  if (options.maxLength != null) {
    result = result.slice(0, options.maxLength);
  }

  return result;
}

  noContinuousNumbers(maxDigits: number = 3): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: string = control.value || '';

      if (!value) return null;

      const regex = new RegExp(`\\d{${maxDigits + 1},}`);

      return regex.test(value)
        ? { continuousNumbers: { maxAllowed: maxDigits } }
        : null;
    };
  }


  formatPhoneNumber(value: string): string {
    if (!value) return value;

    // Remove all non-digits
    const digits = value.replace(/\D/g, '').slice(0, 10);

    // Block same digit repeated (1111111111)
    if (/^(\d)\1{5,}$/.test(digits)) {
      return '';
    }

    // Format: (123) 456-7890
    return digits.replace(
      /^(\d{0,3})(\d{0,3})(\d{0,4}).*/,
      (_match, p1, p2, p3) => {
        let formatted = '';
        if (p1) formatted = `(${p1}`;
        if (p2) formatted += `) ${p2}`;
        if (p3) formatted += `-${p3}`;
        return formatted;
      }
    );
  }

  setHeader(config: HeaderConfig): void {
    this.headerSubject.next(config);
  }

  restrictedEmailValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;

      // Split into local and domain
      const parts = value.split('@');
      if (parts.length !== 2) {
        return { email: true }; // must have exactly one '@'
      }

      const [local, domain] = parts;

      // LOCAL PART VALIDATION
      const localRegex = /^[a-zA-Z0-9]+([._+-]?[a-zA-Z0-9]+)*$/;
      if (!localRegex.test(local)) {
        return { email: true };
      }

      // DOMAIN PART VALIDATION
      const domainRegex = /^(?!-)[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;
      if (!domainRegex.test(domain) || domain.includes('..')) {
        return { email: true };
      }

      // BLOCKED DOMAINS FROM ENVIRONMENT
      const blockedDomains = environment.restrictedEmailDomains;

      if (blockedDomains.includes(domain.toLowerCase())) {
        return { restrictedDomain: true };
      }

      return null;
    };
  }

 optionalMinMaxValidator(min: number, max: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value || '';
    const trimmedValue = value.trim();

    
    if (!trimmedValue) {
      return null;
    }

    // Only spaces
    if (!/\S/.test(value)) {
      return { onlySpaces: true };
    }

    // Length checks
    if (trimmedValue.length < min) {
      return {
        minLength: {
          requiredLength: min,
          actualLength: trimmedValue.length
        }
      };
    }

    if (trimmedValue.length > max) {
      return {
        maxLength: {
          requiredLength: max,
          actualLength: trimmedValue.length
        }
      };
    }

    //  Must contain at least ONE letter
    if (!/[A-Za-z]/.test(trimmedValue)) {
      return { numbersOnly: true };
    }

    //  Invalid characters
    if (!/^[A-Za-z0-9\s,./#-]+$/.test(trimmedValue)) {
      return { pattern: true };
    }

    return null;
  };  
}
  emitHeaderAction(action: string, context?: any): void {
    this.headerActionSubject.next({ action, context });
  }

  agencyNameValidator(
  min = 3,
  max = 100
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = (control.value ?? '').trim();
    if (!value) return { required: true };

    const length = value.length;

    // Length validation
    if (length < min || length > max) {
      return length < min
        ? { minLength: { requiredLength: min, actualLength: length } }
        : { maxLength: { requiredLength: max, actualLength: length } };
    }

    // validation checks
    const rules: { valid: boolean; error: ValidationErrors }[] = [
      {
        valid: /[A-Za-z]/.test(value),
        error: { numbersOnly: true }
      },
      {
        valid: /^[A-Za-z0-9\s&.,'-]+$/.test(value),
        error: { pattern: true }
      },
      {
        valid: !/(\d)\1{3,}|\d{6,}/.test(value),
        error: { continuousNumbers: true }
      }
    ];

    return rules.find(r => !r.valid)?.error ?? null;
  };
}

  public sanitizeHeaderValue(
    value: string | number | null | undefined,
    type: 'generic' | 'name' | 'address' | 'identifier' | 'numeric' = 'generic'
  ): string {
    if (value === null || value === undefined) {
      return '';
    }

    let result = String(value).trim();
    if (!result) {
      return '';
    }

    const patternMap = {
      name: /[^a-zA-Z\s]/g,
      address: /[^a-zA-Z0-9\s,./-]/g,
      identifier: /[^a-zA-Z0-9-]/g,
      numeric: /[^0-9.]/g,
      generic: /[^a-zA-Z0-9\s]/g
    } as const;

    const pattern = patternMap[type] ?? patternMap.generic;
    result = result.replace(pattern, '');
    result = result.replace(/\s{2,}/g, ' ').trim();

    return result;
  }

  // for setting wholesale flag
  
  private _submissionType = signal<string | null>(null);

  // public readonly signals
  submissionType = this._submissionType.asReadonly();

  isWholesale = computed(() =>
    this._submissionType()?.toUpperCase() === 'WHOLESALE'
  );

  setSubmissionType(type: string | null) {
    this._submissionType.set(type);
  }

  
private _skipDirtyGuard = false;

setSkipDirtyGuard(value: boolean): void {
  this._skipDirtyGuard = value;
}

getSkipDirtyGuard(): boolean {
  return this._skipDirtyGuard;
}

clearIdsFromSession() {
  sessionStorage.removeItem('txnId');
  sessionStorage.removeItem('quoteId'); 
}

}
